import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import ThemeToggle from "@/components/ThemeToggle";
import { ArrowRight } from "lucide-react";

export default function SignupPage() {
  const { signup, loading } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", workspace_name: "" });

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      await signup(form);
      toast.success("Workspace created — welcome aboard");
      navigate("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 bg-foreground text-background relative overflow-hidden">
        <Link to="/" className="flex items-center gap-2" data-testid="auth-logo-link">
          <div className="w-7 h-7 rounded-sm bg-background text-foreground flex items-center justify-center font-black tracking-tighter">L</div>
          <span className="font-display font-semibold tracking-tightest">LogiGrowth<span className="text-signal-500">.</span>AI</span>
        </Link>
        <div>
          <div className="label-eyebrow text-background/70 mb-3">Get started in 30 seconds</div>
          <h2 className="font-display font-light text-4xl tracking-tightest leading-[1.05]">
            Spin up your<br />
            <span className="font-black">AI revenue team.</span>
          </h2>
          <p className="mt-6 text-background/70 text-sm leading-relaxed max-w-sm">
            Your new workspace will ship pre-loaded with a demo pipeline — 20 prospects,
            3 campaigns, sample conversations — so you can see the platform in motion immediately.
          </p>
        </div>
        <div className="font-mono text-xs tracking-wider text-background/50">No credit card · 14-day trial</div>
      </div>

      <div className="flex-1 flex flex-col p-6 lg:p-12 bg-background">
        <div className="flex justify-end"><ThemeToggle /></div>
        <div className="flex-1 flex items-center justify-center">
          <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6" data-testid="signup-form">
            <div>
              <div className="label-eyebrow mb-2">Create workspace</div>
              <h1 className="font-display font-semibold text-3xl tracking-tightest">Launch LogiGrowth</h1>
            </div>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-xs">Your name</Label>
                <Input id="name" required value={form.name} onChange={update("name")}
                  data-testid="signup-name-input" placeholder="Anders Berg" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="workspace" className="text-xs">Workspace / Company</Label>
                <Input id="workspace" required value={form.workspace_name} onChange={update("workspace_name")}
                  data-testid="signup-workspace-input" placeholder="Maersk Nordic Logistics" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs">Work email</Label>
                <Input id="email" type="email" required value={form.email} onChange={update("email")}
                  data-testid="signup-email-input" placeholder="you@company.com" autoComplete="email" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-xs">Password</Label>
                <Input id="password" type="password" required minLength={8} value={form.password} onChange={update("password")}
                  data-testid="signup-password-input" placeholder="Min. 8 characters" autoComplete="new-password" />
              </div>
            </div>
            <Button type="submit" disabled={loading} data-testid="signup-submit-btn" className="w-full rounded-sm">
              {loading ? "Creating workspace..." : <>Create workspace <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
            <p className="text-sm text-muted-foreground text-center">
              Already have an account?{" "}
              <Link to="/login" className="text-foreground font-medium hover:underline" data-testid="signup-login-link">Sign in</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
